import axios from "axios";

const TTL_MS = 60 * 1000;
const fresh = (entry) => entry && Date.now() - entry.fetchedAt < TTL_MS;

// Cache keys include clinicUrl — switching clinic within the TTL must never
// serve the previous clinic's data (multi-clinic data isolation).
// They also include the date range so switching startDt/endDt within the TTL
// fetches fresh data instead of serving the previous range.
const rangeSuffix = (startDt, endDt) =>
  startDt || endDt ? `@${startDt || ""}~${endDt || ""}` : "";
const clinicKey = (clinicUrl, startDt, endDt) =>
  `${clinicUrl}${rangeSuffix(startDt, endDt)}`;
const branchKey = (clinicUrl, branchUrl, startDt, endDt) =>
  `${clinicUrl}/${branchUrl}${rangeSuffix(startDt, endDt)}`;
const caseMixKey = (clinicUrl, period) => `${clinicUrl}/${period}`;

const moduleDashboard = {
  namespaced: true,
  state: {
    clinic: {}, // clinicUrl -> { data, fetchedAt }
    branch: {}, // `${clinicUrl}/${branchUrl}` -> { data, fetchedAt }
    caseMixPeriod: {}, // `${clinicUrl}/${period}` -> { data, fetchedAt } (clinic-level lazy tabs)
  },
  mutations: {
    setClinic(state, { key, data }) {
      state.clinic = { ...state.clinic, [key]: { data, fetchedAt: Date.now() } };
    },
    setBranch(state, { key, data }) {
      state.branch = { ...state.branch, [key]: { data, fetchedAt: Date.now() } };
    },
    setCaseMix(state, { key, data }) {
      state.caseMixPeriod = { ...state.caseMixPeriod, [key]: { data, fetchedAt: Date.now() } };
    },
  },
  actions: {
    async fetchClinicDashboard({ state, commit }, { clinicUrl, startDt, endDt, force = false }) {
      const key = clinicKey(clinicUrl, startDt, endDt);
      if (!force && fresh(state.clinic[key])) return state.clinic[key].data;
      const res = await axios.get(`/api/dashboard/${clinicUrl}`, {
        params: { startDt, endDt },
      });
      commit("setClinic", { key, data: res.data.data });
      return res.data.data;
    },
    async fetchBranchDashboard({ state, commit }, { clinicUrl, branchUrl, startDt, endDt, force = false }) {
      const key = branchKey(clinicUrl, branchUrl, startDt, endDt);
      if (!force && fresh(state.branch[key])) return state.branch[key].data;
      const res = await axios.get(`/api/dashboard/${clinicUrl}/${branchUrl}`, {
        params: { startDt, endDt },
      });
      commit("setBranch", { key, data: res.data.data });
      return res.data.data;
    },
    async fetchClinicCaseMix({ state, commit }, { clinicUrl, period }) {
      const key = caseMixKey(clinicUrl, period);
      if (fresh(state.caseMixPeriod[key])) return state.caseMixPeriod[key].data;
      const res = await axios.get(`/api/dashboard/${clinicUrl}/casemix`, { params: { period } });
      commit("setCaseMix", { key, data: res.data.data });
      return res.data.data;
    },
  },
  getters: {
    clinicDashboard: (s) => (clinicUrl, startDt, endDt) => {
      const entry = s.clinic[clinicKey(clinicUrl, startDt, endDt)];
      return entry ? entry.data : null;
    },
    branchDashboard: (s) => (clinicUrl, branchUrl, startDt, endDt) => {
      const entry = s.branch[branchKey(clinicUrl, branchUrl, startDt, endDt)];
      return entry ? entry.data : null;
    },
  },
};

export default moduleDashboard;
